/*
 * Config do assistente de IA.
 *
 * Antes isto vivia em /public/chat-ia-config.js, servido solto e lido em
 * runtime. O <script> apontava para /chat-ia-config.js — absoluto a partir da
 * raiz do dominio —, entao ambiente servido em subpasta acabava lendo o
 * arquivo do vizinho. Pior: config ausente virava objeto vazio, e o
 * assistente caia no chat antigo em silencio, indistinguivel de um rollback
 * proposital.
 *
 * Agora e codigo: versionado, com review e historico. O que varia por
 * ambiente vem do .env (VUE_APP_CHAT_*); o resto e conteudo e mora aqui.
 */

const env = (chave, padrao) => process.env[chave] || padrao

/*
 * Faisca do assistente, a mesma do icone da barra superior, em disco escuro.
 * Vai no `header.logo` do widget — o topo e o avatar das respostas sao
 * imagens INDEPENDENTES: aqui a faisca identifica o assistente, e os baloes
 * seguem com a logo do dominio que o backend manda em agent_avatar.
 *
 * Data URI de proposito: sem arquivo para publicar e sem caminho absoluto
 * para errar em ambiente servido em subpasta.
 */
const FAISCA = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 178 180" width="64" height="64" fill="#111827">
<path d="M117.6.9c-.4.5-1.9 6.1-3.5 12.5-6.2 24.6-14.8 32.9-40.2 39.2-17 4.2-17 6.3-.1 10.5 25.2 6.2 34.6 15.4 40.3 39.4 2.7 11.8 3.6 13.7 5.6 13.3 2.2-.4 2.7-1.8 5.2-12.7 5.5-24.3 15.2-33.8 40.3-40 5.9-1.5 11.2-3.1 11.8-3.7 2.4-2.4-.3-4.1-9.8-6.2-17.9-4.1-26.7-8.9-33.3-18.1-3.9-5.5-9-19-10.4-28.1-.4-2.5-1.2-5.1-1.7-5.8-1.2-1.4-3.4-1.6-4.2-.3m40.8 15.5c-.7 2.9-3.5 5.6-7.2 7.2l-2.7 1.1 3.8 1.6c2.8 1.2 4.3 2.7 5.6 5.7l1.9 4.2 1.8-3.9c1.3-2.5 3.2-4.4 5.8-5.7l3.9-1.9-4.4-2.1c-3.3-1.6-4.6-2.9-5.3-5.4-1.1-3.8-2.3-4.1-3.2-.8"/>
<path d="M62.1 91.1c-.4 1.3-1.2 4-1.6 5.9-2.2 10.5-7.6 15.6-20 19-3.3 1-6 2.3-6 3s3.7 2.5 8.5 4c10.6 3.4 14 7 17.3 17.9 2.2 7.1 4.2 9.9 5.1 6.9.2-.7 1.2-4.1 2.1-7.5 3-10.4 9.2-16 20.5-18.4 7-1.5 6.4-3.7-1.5-5.8-3.3-.9-7.5-2.6-9.3-3.6-4.5-2.7-8.8-9.5-10.3-16.1s-3.4-8.7-4.8-5.3"/>
</svg>`


export const chatIa = {
    /*
     * Provider do assistente: o chat do ClickDesk, em todo ambiente. Fixo de
     * proposito — nao ha variavel para trocar. Voltar para o assistente antigo
     * significa reverter o commit e redeployar.
     */
    provider: 'desk',

    /*
     * Kill-switch do frontend. Fixo em ligado — nao ha variavel para desligar.
     * O desligamento rapido e o DESK_CHAT_HABILITADO do .env do backend, que e
     * relido a cada requisicao e corta a identidade assinada para todo client.
     */
    enabled: true,
    disabledMessage:
        'O Assistente de IA está temporariamente indisponível. Tente novamente em instantes.',

    /*
     * Chat do ClickDesk (provider 'desk'). A URL oficial do widget vem do
     * backend, junto da chave da marca, em /usuarios/chat_identidade — o que
     * esta aqui e so fallback.
     */
    desk: {
        widgetUrl: env('VUE_APP_CHAT_DESK_WIDGET_URL', 'https://app.clickdesk.ai/widget/chat.js'),
        apiUrl: env('VUE_APP_CHAT_DESK_API_URL', 'https://api.desk.click.app/api/v1'),
        title: 'Atendimento inteligente',
        subtitle: 'Consulte dados, tire dúvidas e gere relatórios',
        // Logo do topo. Independente do avatar das respostas, que continua
        // vindo do backend (agent_avatar = favicon do dominio da empresa).
        headerLogo: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(FAISCA),
    },

    /*
     * Assistente antigo (chat-agent). Sai na fase 5 do rollout, junto com o
     * ChatIA.vue e a rota /chat-ia.
     */
    widgetUrl: env('VUE_APP_CHAT_LEGACY_WIDGET_URL', 'https://chat-agent.click.app/widget/latest/chat-ia.js'),
    apiUrl: env('VUE_APP_CHAT_LEGACY_API_URL', 'https://chat-agent.click.app'),
    erpUrl: env('VUE_APP_CHAT_LEGACY_ERP_URL', 'https://app.api.click.app'),
    hideFloatingButton: true,
    welcomeMessage:
`Olá! Sou seu **Assistente de IA**. Posso te ajudar a:

- **Consultar dados** do ERP — clientes, vendas, contas a pagar/receber, estoque, fiscal, e mais
- **Gerar arquivos** com seus dados — Excel, PDF e Word
- **Tirar dúvidas** — como cadastrar, configurar, resolver erros do sistema
- **Analisar telas** — anexe um print do ERP e pergunte
- **Falar por áudio** — clique no microfone pra gravar

Como posso te ajudar hoje?`,
    welcomeSuggestions: [
        'Resumo financeiro do dia',
        'Contas a receber em atraso',
        'Top 10 produtos mais vendidos esse mês',
        'DRE do mês atual',
        'Como cadastrar um cliente novo?',
    ],
}

export default chatIa
